import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import EpisodeCard from "@/components/feature/EpisodeCard";
import EmptyState from "@/components/base/EmptyState";
import LoadingState from "@/components/base/LoadingState";
import { useSeo } from "@/hooks/useSeo";
import { useMockLoading } from "@/hooks/useMockLoading";
import { episodes } from "@/mocks/episodes";
import { getEpisodeTypes, sortByDateAsc, sortByDateDesc } from "@/lib/content";

const PAGE_SIZE = 6;

export default function Category() {
  useSeo({
    title: "朋友的妈妈3剧情介绍分类 - 影视详情与资讯",
    description:
      "朋友的妈妈3剧情介绍分类页，汇集全部剧情章节、幕后花絮与角色解读，支持按类型筛选与发布时间排序，快速定位所需内容。",
    keywords: "朋友的妈妈3分类,朋友的妈妈3剧情列表,朋友的妈妈3章节,朋友的妈妈3花絮",
  });

  const [searchParams] = useSearchParams();
  const initialType = searchParams.get("type") || "全部";

  const [type, setType] = useState(initialType);
  const [sort, setSort] = useState<"desc" | "asc">("desc");
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);
  const loading = useMockLoading(450);

  useEffect(() => {
    setType(initialType);
  }, [initialType]);

  const types = ["全部", ...getEpisodeTypes()];

  const filtered = useMemo(() => {
    const base = type === "全部" ? episodes : episodes.filter((e) => e.type === type);
    return sort === "desc" ? sortByDateDesc(base) : sortByDateAsc(base);
  }, [type, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setType("全部");
    setSort("desc");
    setPage(1);
  };

  const changeType = (value: string) => {
    setType(value);
    setPage(1);
    setPanelOpen(false);
  };

  const changeSort = (value: "desc" | "asc") => {
    setSort(value);
    setPage(1);
  };

  return (
    <>
      <section className="w-full bg-primary-950 px-4 md:px-6 py-10 md:py-14">
        <nav className="w-full text-sm text-background-100/60 mb-4" aria-label="面包屑">
          <ol className="flex items-center gap-2 flex-wrap">
            <li>
              <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">
                首页
              </Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line"></i>
            </li>
            <li className="text-background-50">剧集分类</li>
          </ol>
        </nav>
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">
          剧情介绍分类
        </h1>
        <p className="mt-3 text-sm md:text-base text-background-100/70 max-w-3xl">
          《朋友的妈妈3》第一季共收录 {episodes.length} 条内容，涵盖剧情介绍、幕后花絮与角色解读。
          可按类型筛选，并按发布时间排序，快速找到想看的章节。
        </p>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
        <div className="hidden md:flex items-center justify-between gap-4 mb-8 pb-5 border-b border-background-200/80">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-foreground-500 mr-1">类型</span>
            {types.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => changeType(item)}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  type === item
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm text-foreground-500">排序</span>
            <button
              type="button"
              onClick={() => changeSort("desc")}
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                sort === "desc"
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              最新
            </button>
            <button
              type="button"
              onClick={() => changeSort("asc")}
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                sort === "asc"
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              最旧
            </button>
          </div>
        </div>

        <div className="md:hidden mb-6">
          <button
            type="button"
            onClick={() => setPanelOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line"></i>
              筛选：{type} · {sort === "desc" ? "最新" : "最旧"}
            </span>
            <i className={`ri-arrow-down-s-line transition-transform ${panelOpen ? "rotate-180" : ""}`}></i>
          </button>
          {panelOpen && (
            <div className="mt-3 p-4 rounded-md bg-background-100 border border-background-200 space-y-4">
              <div>
                <p className="text-xs text-foreground-500 mb-2">类型</p>
                <div className="flex flex-wrap gap-2">
                  {types.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => changeType(item)}
                      className={`px-3 py-1.5 rounded-full text-sm cursor-pointer ${
                        type === item
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-50 text-foreground-700"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-2">排序</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => changeSort("desc")}
                    className={`px-3 py-1.5 rounded-full text-sm cursor-pointer ${
                      sort === "desc" ? "bg-secondary-500 text-background-50" : "bg-background-50 text-foreground-700"
                    }`}
                  >
                    最新
                  </button>
                  <button
                    type="button"
                    onClick={() => changeSort("asc")}
                    className={`px-3 py-1.5 rounded-full text-sm cursor-pointer ${
                      sort === "asc" ? "bg-secondary-500 text-background-50" : "bg-background-50 text-foreground-700"
                    }`}
                  >
                    最旧
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <LoadingState text="正在加载内容列表…" />
        ) : paged.length === 0 ? (
          <EmptyState
            icon="ri-search-eye-line"
            title="暂无匹配内容"
            description="当前筛选条件下没有找到相关内容，试试重置筛选条件。"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        ) : (
          <>
            <p className="text-sm text-foreground-500 mb-5">
              共找到 <span className="text-accent-600 font-medium">{filtered.length}</span> 条内容
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {paged.map((episode) => (
                <EpisodeCard key={episode.id} episode={episode} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-2 flex-wrap">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                  className="px-4 py-2 rounded-md text-sm border border-background-300 text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  上一页
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={`w-10 h-10 rounded-md text-sm transition-colors cursor-pointer ${
                      n === currentPage
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setPage(currentPage + 1)}
                  className="px-4 py-2 rounded-md text-sm border border-background-300 text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  下一页
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}